import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/auth';
import { baseAxios } from '../utils/config';
import notify from 'devextreme/ui/notify';
import {
  ROLE_SYSTEM_ADMIN,
  ROLE_JDTECH_ADMIN,
} from '../utils/constants';

// Singleton cache to prevent duplicate API calls across all components
// This persists across component mounts/unmounts and React Strict Mode double renders
const locationsCache = {
  data: null,
  userKey: null,
  promise: null, // Track ongoing request
};

/**
 * Fetches locations based on user role
 * System Admins and JDTech Admins can see all locations
 * Other roles only see their assigned location
 *
 * Uses singleton cache to prevent duplicate API calls even in React Strict Mode
 *
 * @returns {Object} { locations, loading, error }
 */
export const useLocationFetch = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const rolesKey = useMemo(
    () => (user?.userRoles ?? []).join(','),
    [user?.userRoles]
  );
  const assignedLocationsKey = useMemo(
    () =>
      (user?.assignedLocations ?? [])
        .map((location) => `${location.location_id}:${location.location_name ?? ''}`)
        .join(','),
    [user?.assignedLocations]
  );

  // Create stable user identifier to prevent unnecessary re-fetches
  // Only changes when actual user data changes (not object reference)
  const userKey = useMemo(() => {
    if (!user) return null;

    const userIdentifier = user.userID ?? user.email ?? user.barcode ?? 'unknown-user';
    return [
      userIdentifier,
      user.barcode ?? '',
      user.location_id ?? '',
      user.location_name ?? '',
      user.org ?? '',
      rolesKey,
      assignedLocationsKey,
    ].join('|');
  }, [
    user,
    user?.userID,
    user?.email,
    user?.barcode,
    user?.location_id,
    user?.location_name,
    user?.org,
    rolesKey,
    assignedLocationsKey,
  ]);

  useEffect(() => {
    if (!userKey) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchLocations = async () => {
      // The request this call started, if any
      let ownRequest = null;
      try {
        setLoading(true);
        setError(null);

        // Check cache first - if we already have data for this user, use it
        if (locationsCache.userKey === userKey && locationsCache.data) {
          if (isMounted) {
            setLocations(locationsCache.data);
            setLoading(false);
          }
          return;
        }

        // The list on screen belongs to the previous user or organization
        if (isMounted) {
          setLocations([]);
        }

        // If there's already an ongoing request for this user, wait for it
        if (locationsCache.promise && locationsCache.userKey === userKey) {
          const cachedData = await locationsCache.promise;
          if (isMounted) {
            setLocations(cachedData);
            setLoading(false);
          }
          return;
        }

        // Check if user can select any location (admin roles)
        const isSystemAdmin = user.userRoles?.includes(ROLE_SYSTEM_ADMIN);
        const isJDTechAdmin = user.userRoles?.includes(ROLE_JDTECH_ADMIN);
        const canSelectLocation = isSystemAdmin || isJDTechAdmin;

        // Create new request promise
        const requestPromise = (async () => {
          if (canSelectLocation) {
            // Admin users - fetch all locations
            const response = await baseAxios.get('/organization/-/locations');
            return response.data?.locations || [];
          } else if (user.assignedLocations?.length >= 2) {
            // JD-293: Multi-location non-admin users (e.g., front-desk covering 2 salons)
            // Return all assigned locations from the auth response (no API call needed).
            // Cache key (userKey) is per-user, not per-selected-location, so this is
            // stable across location switches — assignedLocations doesn't change on switch.
            return user.assignedLocations;
          } else {
            // Single-location non-admin users (no API call needed)
            return [{
              location_id: user.location_id,
              location_name: user.location_name,
              organization_id: parseInt(user.org),
            }];
          }
        })();

        // Store promise in cache
        ownRequest = requestPromise;
        locationsCache.promise = requestPromise;
        locationsCache.data = null;
        locationsCache.userKey = userKey;

        // Wait for result
        const data = await requestPromise;

        // Cache the result, unless a newer request (another user or
        // organization) has taken over the cache in the meantime
        if (locationsCache.promise === requestPromise) {
          locationsCache.data = data;
          locationsCache.promise = null; // Clear promise after completion
        }

        if (isMounted) {
          setLocations(data);
        }
      } catch (err) {
        if (locationsCache.promise === ownRequest) {
          locationsCache.data = null;
          locationsCache.promise = null;
          locationsCache.userKey = null;
        }

        if (isMounted) {
          const errorMessage = err.response?.data?.message || 'Failed to fetch locations';
          setError(errorMessage);
          notify(errorMessage, 'error', 5000);
          setLocations([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLocations();

    // Cleanup
    return () => {
      isMounted = false;
    };
  }, [userKey, user, rolesKey]);

  return { locations, loading, error };
};

/**
 * Clear the singleton locations cache. Call on signOut to prevent
 * stale data from leaking across user sessions in a long-lived SPA.
 */
export function clearLocationsCache() {
  locationsCache.data = null;
  locationsCache.userKey = null;
  locationsCache.promise = null;
}
